import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './hooks/useAuth';
import Layout from './components/layout/Layout';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import LandingPage from './pages/LandingPage';
import DashboardPage from './pages/DashboardPage';
import AdminPage from './pages/AdminPage';
import DomainOverviewPage from './pages/DomainOverviewPage';
import OrganicResearchPage from './pages/OrganicResearchPage';
import TrafficAnalyticsPage from './pages/TrafficAnalyticsPage';
import KeywordOverviewPage from './pages/KeywordOverviewPage';
import KeywordMagicPage from './pages/KeywordMagicPage';
import KeywordGapPage from './pages/KeywordGapPage';
import BulkKDPage from './pages/BulkKDPage';
import BacklinkOverviewPage from './pages/BacklinkOverviewPage';
import BacklinkGapPage from './pages/BacklinkGapPage';
import BulkBacklinkPage from './pages/BulkBacklinkPage';
import SiteAuditPage from './pages/SiteAuditPage';
import OnPageCheckerPage from './pages/OnPageCheckerPage';
import ContentPage from './pages/ContentPage';
import CheckerPage from './pages/CheckerPage';
import PricingPage from './pages/PricingPage';
import BlogPage from './pages/BlogPage';
import BlogPostPage from './pages/BlogPostPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import TermsPage from './pages/TermsPage';
import AuthCallbackPage from './pages/AuthCallbackPage';
import KeywordResearchPage from './pages/seo/KeywordResearchPage';
import BacklinkCheckerPage from './pages/seo/BacklinkCheckerPage';
import SiteAuditPageSEO from './pages/seo/SiteAuditPageSEO';
import SemrushAlternativePage from './pages/seo/SemrushAlternativePage';
import AISearchPage from './pages/seo/AISearchPage';
import FreeSEOToolsPage from './pages/seo/FreeSEOToolsPage';
import DomainAuthorityPage from './pages/seo/DomainAuthorityPage';
import KeywordDifficultyPage from './pages/seo/KeywordDifficultyPage';
import OnPageCheckerPageSEO from './pages/seo/OnPageCheckerPageSEO';
import AhrefsAlternativePage from './pages/seo/AhrefsAlternativePage';
import FreeKeywordToolPage from './pages/seo/FreeKeywordToolPage';
import RankCheckerPage from './pages/seo/RankCheckerPage';
import TrafficCheckerPage from './pages/seo/TrafficCheckerPage';
import SEOAnalyzerPage from './pages/seo/SEOAnalyzerPage';
import WebsiteTrafficPage from './pages/seo/WebsiteTrafficPage';
import DAPAcheckerPage from './pages/seo/DAPAcheckerPage';
import KeywordDensityPage from './pages/seo/KeywordDensityPage';
import WordCounterPage from './pages/seo/WordCounterPage';
import PlagiarismCheckerPage from './pages/seo/PlagiarismCheckerPage';
import AIDetectorPage from './pages/seo/AIDetectorPage';
import MetaTagGeneratorPage from './pages/seo/MetaTagGeneratorPage';
import RobotsTxtGeneratorPage from './pages/seo/RobotsTxtGeneratorPage';
import SitemapGeneratorPage from './pages/seo/SitemapGeneratorPage';
import ParaphrasingToolPage from './pages/seo/ParaphrasingToolPage';
import GrammarCheckerPage from './pages/seo/GrammarCheckerPage';
import PageSpeedCheckerPage from './pages/seo/PageSpeedCheckerPage';
import WhoisCheckerPage from './pages/seo/WhoisCheckerPage';

function Loading() {
  return (
    <div style={{ display:'flex', alignItems:'center', justifyContent:'center', height:'100vh', background:'#FAFBFC' }}>
      <div className="spinner" style={{ width:28, height:28 }} />
    </div>
  );
}

function Private({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <Loading />;
  return user ? children : <Navigate to="/login" replace />;
}

function Public({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <Loading />;
  return user ? <Navigate to="/dashboard" replace /> : children;
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          {/* Public SEO Landing Pages - Rank on Google */}
          <Route path="/" element={<LandingPage />} />
          <Route path="/login" element={<Public><LoginPage /></Public>} />
          <Route path="/register" element={<Public><RegisterPage /></Public>} />
          <Route path="/blog" element={<BlogPage />} />
          <Route path="/blog/:slug" element={<BlogPostPage />} />
          <Route path="/privacy" element={<PrivacyPolicyPage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/auth/callback" element={<AuthCallbackPage />} />
          <Route path="/keyword-research-tool" element={<KeywordResearchPage />} />
          <Route path="/backlink-checker" element={<BacklinkCheckerPage />} />
          <Route path="/site-audit-tool" element={<SiteAuditPageSEO />} />
          <Route path="/semrush-alternative" element={<SemrushAlternativePage />} />
          <Route path="/ahrefs-alternative" element={<AhrefsAlternativePage />} />
          <Route path="/ai-search" element={<AISearchPage />} />
          <Route path="/seo-tools" element={<FreeSEOToolsPage />} />
          <Route path="/free-seo-tools" element={<FreeSEOToolsPage />} />
          <Route path="/domain-authority-checker" element={<DomainAuthorityPage />} />
          <Route path="/keyword-difficulty-checker" element={<KeywordDifficultyPage />} />
          <Route path="/on-page-seo-checker" element={<OnPageCheckerPageSEO />} />
          <Route path="/ai-search-optimization" element={<AISearchPage />} />
          <Route path="/free-backlink-checker" element={<BacklinkCheckerPage />} />
          <Route path="/free-keyword-research-tool" element={<KeywordResearchPage />} />
          <Route path="/free-keyword-tool" element={<FreeKeywordToolPage />} />
          <Route path="/google-rank-checker" element={<RankCheckerPage />} />
          <Route path="/rank-checker" element={<RankCheckerPage />} />
          <Route path="/serp-checker" element={<RankCheckerPage />} />
          <Route path="/website-traffic-checker" element={<TrafficCheckerPage />} />
          <Route path="/traffic-checker" element={<TrafficCheckerPage />} />
          <Route path="/seo-analyzer" element={<SEOAnalyzerPage />} />
          <Route path="/website-seo-checker" element={<SEOAnalyzerPage />} />
          <Route path="/domain-overview" element={<WebsiteTrafficPage />} />
          <Route path="/website-overview" element={<WebsiteTrafficPage />} />
          <Route path="/da-pa-checker" element={<DAPAcheckerPage />} />
          <Route path="/da-checker" element={<DAPAcheckerPage />} />
          <Route path="/pa-checker" element={<DAPAcheckerPage />} />
          <Route path="/keyword-density-checker" element={<KeywordDensityPage />} />
          <Route path="/word-counter" element={<WordCounterPage />} />
          <Route path="/plagiarism-checker" element={<PlagiarismCheckerPage />} />
          <Route path="/ai-detector" element={<AIDetectorPage />} />
          <Route path="/ai-content-detector" element={<AIDetectorPage />} />
          <Route path="/chatgpt-detector" element={<AIDetectorPage />} />
          <Route path="/meta-tag-generator" element={<MetaTagGeneratorPage />} />
          <Route path="/meta-description-generator" element={<MetaTagGeneratorPage />} />
          <Route path="/robots-txt-generator" element={<RobotsTxtGeneratorPage />} />
          <Route path="/xml-sitemap-generator" element={<SitemapGeneratorPage />} />
          <Route path="/sitemap-generator" element={<SitemapGeneratorPage />} />
          <Route path="/paraphrasing-tool" element={<ParaphrasingToolPage />} />
          <Route path="/article-rewriter" element={<ParaphrasingToolPage />} />
          <Route path="/rewriter-tool" element={<ParaphrasingToolPage />} />
          <Route path="/grammar-checker" element={<GrammarCheckerPage />} />
          <Route path="/grammar-check" element={<GrammarCheckerPage />} />
          <Route path="/spell-checker" element={<GrammarCheckerPage />} />
          <Route path="/page-speed-checker" element={<PageSpeedCheckerPage />} />
          <Route path="/website-speed-test" element={<PageSpeedCheckerPage />} />
          <Route path="/pagespeed-checker" element={<PageSpeedCheckerPage />} />
          <Route path="/site-speed-checker" element={<PageSpeedCheckerPage />} />
          <Route path="/whois-checker" element={<WhoisCheckerPage />} />
          <Route path="/whois-lookup" element={<WhoisCheckerPage />} />
          <Route path="/ip-lookup" element={<WhoisCheckerPage />} />
          <Route path="/what-is-my-ip" element={<WhoisCheckerPage />} />

          {/* App - Protected */}
          <Route element={<Private><Layout /></Private>}>
            <Route path="/dashboard" element={<DashboardPage />} />
            <Route path="/admin" element={<AdminPage />} />
            <Route path="/domain/overview" element={<DomainOverviewPage />} />
            <Route path="/domain/organic" element={<OrganicResearchPage />} />
            <Route path="/domain/traffic" element={<TrafficAnalyticsPage />} />
            <Route path="/keywords/overview" element={<KeywordOverviewPage />} />
            <Route path="/keywords/magic" element={<KeywordMagicPage />} />
            <Route path="/keywords/gap" element={<KeywordGapPage />} />
            <Route path="/keywords/difficulty" element={<BulkKDPage />} />
            <Route path="/backlinks/overview" element={<BacklinkOverviewPage />} />
            <Route path="/backlinks/gap" element={<BacklinkGapPage />} />
            <Route path="/backlinks/bulk" element={<BulkBacklinkPage />} />
            <Route path="/audit/site" element={<SiteAuditPage />} />
            <Route path="/audit/onpage" element={<OnPageCheckerPage />} />
            <Route path="/content" element={<ContentPage />} />
            <Route path="/checker" element={<CheckerPage />} />
            <Route path="/pricing" element={<PricingPage />} />
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}
